import { useState, useEffect, useRef } from 'react';
import { Search, FileText, Leaf, FlaskConical, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDatabase } from '@/hooks/useDatabase';
import { useLocation } from 'wouter';

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { database } = useDatabase();
  const [, setLocation] = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const studies = database?.studies || [];
  const mushrooms = database?.mushrooms || [];
  const compounds = database?.compounds || [];

  // Search logic
  const searchResults = query.trim().length > 0 ? {
    studies: studies.filter(study => 
      study.title.toLowerCase().includes(query.toLowerCase()) ||
      study.authors?.toLowerCase().includes(query.toLowerCase()) ||
      study.journal?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5),
    mushrooms: mushrooms.filter(mushroom =>
      mushroom.common_name.toLowerCase().includes(query.toLowerCase()) ||
      mushroom.scientific_name.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5),
    compounds: compounds.filter(compound =>
      compound.name.toLowerCase().includes(query.toLowerCase()) ||
      compound.category?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5)
  } : { studies: [], mushrooms: [], compounds: [] };

  const totalResults = searchResults.studies.length + searchResults.mushrooms.length + searchResults.compounds.length;

  // Flatten results for keyboard navigation
  const allResults = [
    ...searchResults.studies.map(s => ({ type: 'study', id: s.study_id, title: s.title })),
    ...searchResults.mushrooms.map(m => ({ type: 'mushroom', id: m.mushroom_id, title: m.common_name })),
    ...searchResults.compounds.map(c => ({ type: 'compound', id: c.compound_id, title: c.name }))
  ];

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }

      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }

      // Arrow navigation when dropdown is open
      if (isOpen && allResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % allResults.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + allResults.length) % allResults.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          handleSelectResult(allResults[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allResults, selectedIndex]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelectResult = (result: { type: string; id: string; title: string }) => {
    if (result.type === 'study') {
      setLocation(`/study/${result.id}`);
    } else if (result.type === 'mushroom') {
      setLocation(`/?mushroom=${result.id}`);
    } else if (result.type === 'compound') {
      setLocation(`/compounds?highlight=${result.id}`);
    }
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E6B64]" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search trials, species, compounds... (⌘K)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-10 bg-white border-[#E4E1D8] focus:border-[#A98A2C] focus:ring-[#A98A2C]"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6B64] hover:text-[#2A2A28]"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && query.trim().length > 0 && (
        <Card className="absolute top-full mt-2 w-full max-h-96 overflow-y-auto z-50 border-[#E4E1D8] shadow-xl">
          {totalResults === 0 ? (
            <div className="p-4 text-center text-[#6E6B64]">
              No results found for "{query}"
            </div>
          ) : (
            <div className="py-2">
              {/* Studies */}
              {searchResults.studies.length > 0 && (
                <div className="mb-2">
                  <div className="px-4 py-2 text-xs font-semibold text-[#6E6B64] uppercase tracking-wide">
                    Clinical Trials ({searchResults.studies.length})
                  </div>
                  {searchResults.studies.map((study, idx) => {
                    const globalIdx = idx;
                    return (
                      <button
                        key={study.study_id}
                        onClick={() => handleSelectResult({ type: 'study', id: study.study_id, title: study.title })}
                        className={`w-full text-left px-4 py-2 hover:bg-[#F4F2EC] transition-colors ${
                          selectedIndex === globalIdx ? 'bg-[#F4F2EC]' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-[#A98A2C] mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2A28] truncate">{study.title}</p>
                            <p className="text-xs text-[#6E6B64] truncate">{study.journal} • {study.year}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Mushroom Species */}
              {searchResults.mushrooms.length > 0 && (
                <div className="mb-2">
                  <div className="px-4 py-2 text-xs font-semibold text-[#6E6B64] uppercase tracking-wide">
                    Mushroom Species ({searchResults.mushrooms.length})
                  </div>
                  {searchResults.mushrooms.map((mushroom, idx) => {
                    const globalIdx = searchResults.studies.length + idx;
                    return (
                      <button
                        key={mushroom.mushroom_id}
                        onClick={() => handleSelectResult({ type: 'mushroom', id: mushroom.mushroom_id, title: mushroom.common_name })}
                        className={`w-full text-left px-4 py-2 hover:bg-[#F4F2EC] transition-colors ${
                          selectedIndex === globalIdx ? 'bg-[#F4F2EC]' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <Leaf className="w-4 h-4 text-[#10B981] mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2A28]">{mushroom.common_name}</p>
                            <p className="text-xs text-[#6E6B64] italic">{mushroom.scientific_name}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Compounds */}
              {searchResults.compounds.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-[#6E6B64] uppercase tracking-wide">
                    Bioactive Compounds ({searchResults.compounds.length})
                  </div>
                  {searchResults.compounds.map((compound, idx) => {
                    const globalIdx = searchResults.studies.length + searchResults.mushrooms.length + idx;
                    return (
                      <button
                        key={compound.compound_id}
                        onClick={() => handleSelectResult({ type: 'compound', id: compound.compound_id, title: compound.name })}
                        className={`w-full text-left px-4 py-2 hover:bg-[#F4F2EC] transition-colors ${
                          selectedIndex === globalIdx ? 'bg-[#F4F2EC]' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <FlaskConical className="w-4 h-4 text-[#C26A1E] mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2A28]">{compound.name}</p>
                            {compound.category && (
                              <Badge variant="secondary" className="text-xs mt-1">
                                {compound.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
